/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type AbstractControl, type ValidationErrors, type ValidatorFn } from '@angular/forms';
import {
    type BaseRequestOptionsArgs, type Command, type Filter, type FormControl, type HttpResponse, type Params,
    type Rest, BatchService,
    ExtendService, HttpService, PromptService, UtilService,
} from '@dm/core';
import { INVALID_VSTORE_ID } from 'app/common/constants';
import { MO } from 'app/common/constants/mo';
import { ToolService } from 'app/common/services';
import { type Host, type HostGroup } from 'app/common/types/mo';
import { FeatureConfig } from 'app/config/feature';
import { cloneDeep } from 'lodash';
import { type Observable, of, throwError } from 'rxjs';
import { map } from 'rxjs/operators';

import { type CreateInitiatorParams } from './create-initiator/initiator';
import { HostsStrService } from './hosts-str.service';

type InitiatorType = MO.FC_TYPE | MO.ISCSI_TYPE | MO.NVMeoF_RoCE_TYPE;

@Injectable()
export class HostsService {
    i18nLabel = this.str.i18nLabel;
    promptTip = this.str.promptTip;
    restTip = this.str.restTip;
    initiatorTypeRestMo = this.str.initiatorTypeRestMo;

    constructor(
        public http: HttpService,
        private ext: ExtendService,
        private str: HostsStrService,
        private batch: BatchService,
        private prompt: PromptService,
        private utilService: UtilService,
        private toolService: ToolService,
    ) { }

    // 查询UltraPath主机开关
    getUltrapathHost(): Observable<HttpResponse> {
        const rest = this.http.makeUrl('host.modifyScanSwitch').setParams({ NAME: '1' });
        return this.http.get(rest);
    }

    /**
     * 映射移除端口组
     * @param type 对象类型 主机或主机组
     */
    removePortGroup(params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('mapping.removePortGroup');
        return this.http.put(rest, params, options);
    }

    /**
     * 获取下一个可以ID
     * @param type 对象类型 主机或主机组
     */
    getNextAvailableId(type: number): Observable<HttpResponse> {
        const typeStr = type === MO.HOST_TYPE ? 'host' : 'hostGroup';
        const rest = this.http.makeUrl(`${typeStr}.nextavailableid`).setParams({ TYPE: type });
        return this.http.get(rest, { mask: true, doException: false });
    }

    /**
     * 自动扫描创建主机
     */
    scanHost(): Observable<HttpResponse> {
        const rest = this.http.makeUrl('host.scan');
        return this.http.get(rest, { mask: true, doSuccess: true, doException: true });
    }

    /**
     * 生成批量查询主机的rest
     * @param isUnaddOnly 是否只获取未加入主机组的主机
     */
    getBatchHostRest(isUnaddOnly?: boolean, vstoreId?: string): Rest {
        const rest = this.http.makeUrl('host.getBatch', undefined, true).setParams({ vstoreId });
        if (isUnaddOnly) {
            rest.setFilters([{ field: 'ISADD2HOSTGROUP', matchMode: '::', value: 'false' }]);
        }

        return rest;
    }

    /**
     * 批量查询主机
     */
    getBatchHost(): Observable<HttpResponse> {
        const rest = this.http.makeUrl('host.getBatch');
        return this.http.get(rest, { doException: false });
    }

    /**
     * 创建主机
     * @param data 创建参数
     * @param opt 请求选项
     */
    createHost(data: Host, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('host.post');
        return this.http.post(rest, data, opt);
    }

    /**
     * 查询主机详情
     * @param id 主机ID
     */
    getHost(id: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('host.get').format(id);
        return this.http.get(rest);
    }

    /**
     * 修改主机
     * @param hostId 主机ID
     * @param data 修改参数
     * @param opt 请求选项
     */
    modifyHost(hostId: string, data: Host, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('host.put').format(hostId);
        return this.http.put(rest, data, opt);
    }

    /**
     * 修改主机所属租户
     * @param hostId 主机ID
     * @param data 修改参数
     * @param opt 请求选项
     */
    modifyHostVstore(data: { ID: string; newVstoreId: string }, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('host.modifyVstore');
        return this.http.put(rest, data, opt);
    }

    /**
     * 生成删除主机的请求
     * @param id 主机ID
     * @param opt 请求选项
     */
    deleteHostReq(id: string, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('host.delete').format(id);
        return this.http.delete(rest, opt);
    }

    /**
     * 生成批量查询主机组的rest
     */
    getBatchHostGroupRest(): Rest {
        return this.http.makeUrl('hostGroup.getBatch', undefined, true);
    }

    /**
     * 批量查询主机组
     */
    getBatchHostGroup(): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hostGroup.getBatch');
        return this.http.get(rest, { doException: false });
    }

    /**
     * 创建主机组
     * @param data 创建参数
     * @param opt 请求选项
     */
    createHostGroup(data: HostGroup, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hostGroup.post');
        return this.http.post(rest, data, opt);
    }

    /**
     * 查询主机组详情
     * @param id 主机组ID
     */
    getHostGroup(id: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hostGroup.get').format(id);
        return this.http.get(rest, { mask: true });
    }

    /**
     * 修改主机组
     * @param groupId 主机组ID
     * @param data 修改参数
     * @param opt 请求选项
     */
    modifyHostGroup(groupId: string, data: HostGroup, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hostGroup.put').format(groupId);
        return this.http.put(rest, data, opt);
    }

    /**
     * 生成删除主机组的请求
     * @param id 主机组ID
     * @param opt 请求参数
     */
    deleteHostGroupReq(id: string, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hostGroup.delete').format(id);
        return this.http.delete(rest, opt);
    }

    /**
     * 向指定主机组添加指定主机
     * @param groupId 主机组ID
     * @param hostId 主机ID
     * @param opt 请求选项
     */
    addHost(groupId: string, hostId: string, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hostGroup.addHost');
        const data = {
            ID: groupId,
            ASSOCIATEOBJTYPE: MO.HOST_TYPE,
            ASSOCIATEOBJID: hostId,
        };
        return this.http.post(rest, data, opt);
    }

    /**
     * 从指定主机组移除指定主机
     * @param groupId 主机组ID
     * @param hostId 主机ID
     */
    removeHost(groupId: string, hostId: string, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('host.deleteAssociate');
        rest.setParams({
            ID: groupId,
            ASSOCIATEOBJTYPE: MO.HOST_TYPE,
            ASSOCIATEOBJID: hostId,
        });
        return this.http.delete(rest, opt);
    }

    /**
     * 生成查询指定主机组所关联的主机的rest
     * @param groupId 主机组ID
     */
    getGroupHostsRest(groupId: string): Rest {
        const rest = this.http.makeUrl('host.getAssociate');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.HOST_GROUP_TYPE,
            ASSOCIATEOBJID: groupId,
        });

        return rest;
    }

    /**
     * 查询指定主机组所关联的主机
     * @param groupId 主机组ID
     */
    getGroupHosts(groupId: string): Observable<HttpResponse> {
        const rest = this.getGroupHostsRest(groupId);
        return this.http.get(rest, { mask: true });
    }

    /**
     * 生成查询指定主机组内主机数量的rest
     * @param groupId 主机组ID
     */
    getGroupHostsCountRest(groupId: string): Rest {
        const rest = this.http.makeUrl('host.count');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.HOST_GROUP_TYPE,
            ASSOCIATEOBJID: groupId,
        });

        return rest;
    }

    /**
     * 查询指定主机所关联的启动器
     * @param hostId 主机ID
     */
    getHostInitiators = (hostId: string, opt?: BaseRequestOptionsArgs): Observable<any[]> => {
        const fcRest = this.http.makeUrl('fcInitiator.getBatch').setParams({ PARENTID: hostId });
        const iscsiRest = this.http.makeUrl('iscsiInitiator.getBatch').setParams({ PARENTID: hostId });
        const nvmeofRoceRest = this.http.makeUrl('nvmeofRoceInitiator.getBatch').setParams({ PARENTID: hostId });
        // 微存储直通屏蔽fc
        const fc$ = !FeatureConfig.IsSupportFC ? of(null) : this.http.get(fcRest, opt);
        // 微存储直通屏蔽iscsi
        const iscsi$ = !FeatureConfig.IsSupportISCSI ? of(null) : this.http.get(iscsiRest, opt);
        // 入门级存储屏蔽nvmeofRoce启动器
        const nvmeofRoce$ = !FeatureConfig.IsSupportNVMEOverRoCENetWorkAndLif ? of(null) : this.http.get(nvmeofRoceRest, opt);

        return this.ext.forkAll([fc$, iscsi$, nvmeofRoce$]).pipe(
            map(([fcRes, iscsiRes, nvmeofRoceRes]) => [...(fcRes && fcRes.data || []), ...(iscsiRes && iscsiRes.data || []),
            ...(nvmeofRoceRes && nvmeofRoceRes.data || [])]),
        );
    };

    /**
     * 生成获取可添加的启动器的rest
     * @param type 启动器类型
     */
    getFreeInitiatorRest(type: InitiatorType, vstoreId?: string): Rest {
        const typeStr = this.initiatorTypeRestMo[Number(type)];
        const rest = this.http.makeUrl(`${typeStr}.getBatch`).setParams({
            ISFREE: true,
        });

        if (vstoreId !== undefined) {
            rest.setParams({
                vstoreId,
            });
        }

        return rest;
    }

    getFcPortFilter(params: Params, vstoreId: string, showNoVstoreFc: boolean): Params {
        const oriFilters: Filter[] = cloneDeep(params.filters as Filter[]);

        // 微存储屏蔽多租户
        if (!FeatureConfig.IsSupportVStoreService) {
            return params;
        }

        // 涉及OR过滤，需要全部转换成二维数组进行处理
        if (!showNoVstoreFc) {
            // 只过滤同一租户
            params.filters = [[
                ...oriFilters,
                { field: 'vstoreId', matchMode: '::', value: vstoreId },
            ]];
        } else {
            // 过滤同一租户 + 未归属租户
            params.filters = [
                [
                    ...oriFilters,
                    { field: 'vstoreId', matchMode: '::', value: vstoreId },
                ],
                [
                    ...oriFilters,
                    { field: 'vstoreId', matchMode: '::', value: INVALID_VSTORE_ID },
                ],
            ];
        }

        return params;
    }

    /**
     * 向指定主机添加指定启动器
     * @param hostId 主机ID
     * @param initiatorId 启动器ID
     * @param type 启动器类型
     */
    addInitiator(hostId: string, initiatorId: string, type: InitiatorType, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const typeStr = this.initiatorTypeRestMo[Number(type)];
        const decodeId = this.utilService.decodeHTML(initiatorId);
        const encodeId = encodeURIComponent(decodeId);
        let rest;
        let data = {};
        if (Number(type) === MO.NVMeoF_RoCE_TYPE) {
            rest = this.http.makeUrl(`${typeStr}.add`);
            data = {
                ID: hostId,
                ASSOCIATEOBJID: decodeId,
                ASSOCIATEOBJTYPE: MO.NVMeoF_RoCE_TYPE,
            };
        } else {
            rest = this.http.makeUrl(`${typeStr}.put`).format(encodeId);
            data = {
                ID: decodeId,
                PARENTID: hostId,
                PARENTTYPE: MO.HOST_TYPE,
            };
        }
        return this.http.put(rest, data, opt);
    }

    /**
     * 创建启动器
     * @param data 创建参数
     * @param type 启动器类型
     * @param opt 请求选项
     */
    createInitiator(data: CreateInitiatorParams, type: InitiatorType, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const typeStr = this.initiatorTypeRestMo[Number(type)];
        const rest = this.http.makeUrl(`${typeStr}.post`);
        return this.http.post(rest, data, opt);
    }

    /**
     * 查询启动器详情
     * @param id 启动器ID
     * @param type 启动器类型
     */
    getInitiator(id: string, type: InitiatorType): Observable<HttpResponse> {
        const typeStr = this.initiatorTypeRestMo[Number(type)];
        const rest = this.http.makeUrl(`${typeStr}.get`).format(id);
        return this.http.get(rest);
    }

    /**
     * 批量查询启动器并且根据id精确过滤
     * @param id 启动器ID
     * @param type 启动器类型
     */
    getBatchInitiatorById(id: string, type: InitiatorType, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const typeStr = this.initiatorTypeRestMo[Number(type)];
        const rest = this.http.makeUrl(`${typeStr}.getBatch`).setFilters([
            { field: 'ID', matchMode: '::', value: id },
        ]);
        return this.http.get(rest, opt);
    }

    /**
     * 查询所有启动器详情
     * @param type 启动器类型
     */
    getAllInitiatorRest(type: InitiatorType): Rest {
        const typeStr = this.initiatorTypeRestMo[Number(type)];
        const rest = this.http.makeUrl(`${typeStr}.getBatch`, undefined, true);
        return rest;
    }

    /**
     * 修改启动器
     * @param id 启动器ID
     * @param data 修改参数
     * @param type 启动器类型
     * @param opt 请求选项
     */
    modifyInitiator(data: CreateInitiatorParams, type: InitiatorType, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const typeStr = this.initiatorTypeRestMo[Number(type)];
        const rest = this.http.makeUrl(`${typeStr}.put`);
        return this.http.put(rest, data, opt);
    }

    /**
     * 修改启动器所属租户
     * @param data 修改参数
     * @param type 启动器类型
     * @param opt 请求选项
     */
    modifyInitiatorVstore(data: { ID: string; newVstoreId: any }, type: InitiatorType, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const typeStr = this.initiatorTypeRestMo[Number(type)];
        const rest = this.http.makeUrl(`${typeStr}.modifyVstore`);
        return this.http.put(rest, data, opt);
    }

    /**
     * 删除启动器
     * @param id 启动器ID
     * @param type 启动器类型
     * @param opt 请求选项
     */
    deleteInitiator(id: string, type: InitiatorType, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const typeStr = this.initiatorTypeRestMo[Number(type)];
        const rest = this.http.makeUrl(`${typeStr}.delete`).format(id);
        return this.http.delete(rest, opt);
    }

    /**
     * 批量删除启动器
     * @param hosts 启动器对象数组
     * @param callback 弹出框关闭的回调函数
     */
    batchDeleteInitiatorAction(initiators: Array<any>, callback: (changed: boolean) => void): void {
        const nameStr = initiators.map((initiator) => initiator.ID).join(', ');
        this.prompt.warning({
            id: 'prompt-host-batchDeleteInitiatorAction',
            title: this.i18nLabel.deleteInitiator,
            content: this.promptTip.deleteInitiator.replace('{0}', nameStr),
            onOk: () => {
                this.prompt.close();
                const cmds: Command[] = [];
                initiators.map((initiator) => {
                    const deleteInitiator: Command = {
                        name: this.restTip.deleteInitiator(initiator),
                        action: this.deleteInitiator(encodeURIComponent(this.utilService.decodeHTML(initiator.ID)),
                            initiator.TYPE, { doException: false }),
                    };
                    cmds.push(deleteInitiator);
                });
                this.batch.exePrompt(cmds, {
                    onClose: () => callback(true),
                });
            },
            onCancel: () => {
                this.prompt.close();
                callback(false);
            },
        });
    }

    /**
     * （从主机）移除启动器
     * @param id 启动器ID
     * @param type 启动器类型
     * @param opt 请求选项
     */
    removeInitiator(id: string, type: InitiatorType, opt?: BaseRequestOptionsArgs, hostId?: string): Observable<HttpResponse> {
        const typeStr = this.initiatorTypeRestMo[Number(type)];
        const rest = this.http.makeUrl(`${typeStr}.remove`);
        const decodeId = this.utilService.decodeHTML(id);

        let data = {};
        if (Number(type) === MO.NVMeoF_RoCE_TYPE && hostId) {
            data = {
                ID: hostId,
                ASSOCIATEOBJID: decodeId,
                ASSOCIATEOBJTYPE: MO.NVMeoF_RoCE_TYPE,
            };
        } else {
            data = {
                ID: decodeId,
            };
        }

        return this.http.put(rest, data, opt);
    }

    /**
     * 生成查询主机映射的LUN的rest
     * @param hostId 主机ID
     */
    getHostMapingLunRest(hostId: string): Rest {
        const rest = this.http.makeUrl('mapping.mappedObjects')
            .setParams({
                ASSOCIATEOBJTYPE: MO.HOST_TYPE,
                ASSOCIATEOBJID: hostId,
            });
        return rest;
    }

    /**
     * 生成查询主机组映射的LUN的rest
     * @param hostGroupId 主机组ID
     */
    getHostGroupMapingLunRest(hostGroupId: string): Rest {
        const rest = this.http.makeUrl('mapping.mappedObjects')
            .setParams({
                ASSOCIATEOBJTYPE: MO.HOST_GROUP_TYPE,
                ASSOCIATEOBJID: hostGroupId,
            })
            .setFilters([
                { field: 'lunGroupId', matchMode: '!!', value: '-1' },
            ]);
        return rest;
    }

    /**
     * 生成查询主机映射的LUN数量的rest
     * @param hostId 主机ID
     */
    getHostMapingLunCountRest(hostId: string): Rest {
        const rest = this.http.makeUrl('mapping.mappedObjectsCount')
            .setParams({
                ASSOCIATEOBJTYPE: MO.HOST_TYPE,
                ASSOCIATEOBJID: hostId,
            });
        return rest;
    }

    /**
     * 生成查询主机组映射的LUN数量的rest
     * @param hostGroupId 主机组ID
     */
    getHostGroupMapingLunCountRest(hostGroupId: string): Rest {
        const rest = this.http.makeUrl('mapping.mappedObjectsCount')
            .setParams({
                ASSOCIATEOBJTYPE: MO.HOST_GROUP_TYPE,
                ASSOCIATEOBJID: hostGroupId,
            })
            .setFilters([
                { field: 'lunGroupId', matchMode: '!!', value: '-1' },
            ]);
        return rest;
    }

    /**
     * 生成查询指定主机映射的LUN的Host Lun ID的Rest对象
     * @param hostId 主机ID
     */
    getHostMappingLunIdRest(hostId: string): Rest {
        return this.http.makeUrl('mapping.hostLunInfo').setParams({ hostId });
    }

    /**
     * 生成查询指定主机组映射的LUN的Host Lun ID的Rest对象
     * @param hostId 主机组ID
     */
    getHostGroupMappingLunIdRest(hostGroupId: string): Rest {
        return this.http.makeUrl('mapping.hostLunInfo').setParams({ hostGroupId });
    }

    /**
     * 查询主机链路
     * @param hostId 主机ID
     */
    getHostPath(hostId: string): Observable<any[]> {
        const fcRest = this.http.makeUrl('hostLink.getBatch')
            .setParams({
                PARENTID: hostId,
                INITIATOR_TYPE: MO.FC_TYPE,
            });
        const iscsiRest = this.http.makeUrl('hostLink.getBatch')
            .setParams({
                PARENTID: hostId,
                INITIATOR_TYPE: MO.ISCSI_TYPE,
            });
        const nvmeofRoceRest = this.http.makeUrl('hostLink.getBatch')
            .setParams({
                PARENTID: hostId,
                INITIATOR_TYPE: MO.NVMeoF_RoCE_TYPE,
            });
        const fc$ = this.http.get(fcRest);
        const iscsi$ = this.http.get(iscsiRest);
        const nvmeofRoce$ = this.http.get(nvmeofRoceRest);


        return this.ext.forkAll([fc$, iscsi$, nvmeofRoce$]).pipe(
            map(([fcRes, iscsiRes, nvmeofRoceRes]) => [...(fcRes.data || []), ...(iscsiRes.data || []),
            ...(nvmeofRoceRes.data || [])]),
        );
    }

    /**
     * 生成可以查询主机链路信息的Rest对象
     * @param hostId 主机ID
     */
    getHostLinkRest(hostId: string): Rest {
        return this.http.makeUrl('hostLink.getBatch').setParams({ PARENTID: hostId });
    }

    /**
     * 删除主机
     * @param host 主机数据对象
     * @param callback 弹出框关闭的回调函数
     */
    deleteHostAction(host: Host, callback: (changed: boolean) => void): void {
        this.prompt.warning({
            id: 'prompt-host-deleteHostAction',
            title: this.i18nLabel.deleteHost,
            content: this.promptTip.deleteHost.replace('{0}', host.NAME),
            onOk: () => {
                this.prompt.close();
                this.deleteHostCmd(host.ID, host.NAME).subscribe((cmds) => {
                    this.batch.exePrompt(cmds, {
                        onClose: () => callback(true),
                    });
                });
            },
            onCancel: () => {
                this.prompt.close();
                callback(false);
            },
        });
    }

    /**
     * 批量删除主机
     * @param hosts 主机对象数组
     * @param callback 弹出框关闭的回调函数
     */
    batchDeleteHostAction(hosts: Array<any>, callback: (changed: boolean, actionType?: string) => void): void {
        const nameStr = hosts.map((host) => host.NAME).join(', ');
        this.prompt.warning({
            id: 'prompt-host-batchDeleteHostAction',
            title: this.i18nLabel.deleteHost,
            content: this.promptTip.deleteHost.replace('{0}', nameStr),
            onOk: () => {
                this.prompt.close();

                const reqs$ = hosts.map((host) => {
                    const simpleCmd: Command = {
                        name: this.restTip.deleteHost(host.NAME),
                        // 删除已加入主机组的主机直接返回错误
                        action: throwError({
                            result: {
                                code: -100,
                                description: this.promptTip.deleteHostErr,
                            },
                        }),
                    };

                    return host.ISADD2HOSTGROUP === 'true' || host.ISADD2HOSTGROUP === true ?
                        of([simpleCmd]) :
                        this.deleteHostCmd(host.ID, host.NAME);
                });

                const cmds = [];
                this.ext.forkAll(reqs$).subscribe((resArr: Array<any>) => {
                    resArr.forEach((res) => cmds.push(...res));

                    this.batch.exePrompt(cmds, {
                        onClose: () => callback(true, 'delete'),
                    });
                });
            },
            onCancel: () => {
                this.prompt.close();
                callback(false);
            },
        });
    }

    /**
     * 生成删除主机的批量执行命令
     * @param hostId 主机ID
     * @param hostName 主机名称
     */
    private deleteHostCmd(hostId: string, hostName: string): Observable<Command[]> {
        // 先查询关联启动器
        return this.getHostInitiators(hostId, { mask: true }).pipe(
            map((initiators) => {
                const cmds: Command[] = [];
                const deps = [];

                // 需先移除启动器
                initiators.forEach((initiator, index) => {
                    const id = hostId + 'removeInitiator' + index;
                    const cmd: Command = {
                        id,
                        name: this.restTip.removeInitiator(this.utilService.decodeHTML(initiator.ID)),
                        action: this.removeInitiator(initiator.ID, initiator.TYPE, { doException: false }, hostId),
                    };
                    cmds.push(cmd);
                    deps.push(id);
                });

                const deleteHost: Command = {
                    deps,
                    force: true,
                    name: this.restTip.deleteHost(hostName),
                    action: this.deleteHostReq(hostId, { doException: false }),
                };

                cmds.push(deleteHost);

                return cmds;
            }),
        );
    }

    /**
     * 删除主机组
     * @param hostGroup 主机组数据对象
     * @param callback 弹出框关闭的回调函数
     */
    deleteHostGroupAction(hostGroup: HostGroup, callback: (changed: boolean) => void): void {
        if (hostGroup.ISADD2MAPPINGVIEW === 'true') {
            this.prompt.fail({
                title: this.i18nLabel.deleteHostGroup,
                content: this.promptTip.deleteHostGroupErr,
            });
        } else {
            this.prompt.warning({
                id: 'prompt-host-deleteHostGroupAction',
                title: this.i18nLabel.deleteHostGroup,
                content: this.promptTip.deleteHostGroup.replace('{0}', hostGroup.NAME),
                onOk: () => {
                    this.prompt.close();
                    this.deleteHostGroupCmd(hostGroup.ID, hostGroup.NAME).subscribe((cmds) => {
                        this.batch.exePrompt(cmds, {
                            onClose: () => callback(true),
                        });
                    });
                },
                onCancel: () => {
                    this.prompt.close();
                    callback(false);
                },
            });
        }
    }

    /**
     * 生成删除主机组的批量执行命令
     * @param groupId 主机组ID
     * @param groupName 主机组名称
     */
    private deleteHostGroupCmd(groupId: string, groupName: string): Observable<Command[]> {
        // 先查询组内主机
        return this.getGroupHosts(groupId).pipe(
            map((res) => {
                const cmds: Command[] = [];
                const deps = [];

                // 组内有主机则需先移除主机
                const hosts = res.data || [];
                hosts.forEach((host, index) => {
                    const id = groupId + 'removeHost' + index;
                    const cmd: Command = {
                        id,
                        name: this.restTip.removeHostFromGroup(host.NAME),
                        action: this.removeHost(groupId, host.ID, { doException: false }),
                    };
                    cmds.push(cmd);
                    deps.push(id);
                });

                const deleteGroup: Command = {
                    deps,
                    force: true,
                    name: this.restTip.deleteHostGroup(groupName),
                    action: this.deleteHostGroupReq(groupId, { doException: false }),
                };

                cmds.push(deleteGroup);

                return cmds;
            }),
        );
    }

    /**
     * 验证WWPN合法性（16位16进制数字）
     * @param control 表单控件
     */
    validateWWPN(control: AbstractControl): ValidationErrors {
        const reg = /(?!(0{16}|F{16}))^[0-9a-f]{16}$/ig;
        if (!reg.test(control.value)) {
            return { wwpn: true };
        } else {
            return null;
        }
    }

    /**
     * 验证IQN合法性（ASCII可见字符，且以字母或数字开头）
     * @param control 表单控件
     */
    validateIQN(control: AbstractControl): ValidationErrors {
        const valueStr: string = control.value.toString();

        // 以字母或数字开头
        const reg = /^[0-9a-z]{1}/i;
        if (!reg.test(valueStr)) {
            return { start: true };
        }

        // 逐个比对是否有ASCII可见字符之外的
        for (let i = 0; i < valueStr.length; i++) {
            const code = valueStr.charCodeAt(i);
            // ASCII可见字符：33~126（不包含空格32）
            if (code < 33 || code > 126) {
                return { ascii: true };
            }
        }

        return null;
    }

    /**
     * 验证Chap名称字段合法性：
     * 只能由数字、字母或特殊字符(`~!@#$%^&*()-_=+|[{}];:<,.\>/?)组成，首字符只能是字母或数字
     * @param control 表单控件
     */
    validateChapName(control: AbstractControl): ValidationErrors {
        const valueStr: string = control.value.toString();

        // 以字母或数字开头
        const startReg = /^[0-9a-z]{1}/i;
        if (!startReg.test(valueStr)) {
            return { start: true };
        }

        // 由数字、字母或特殊字符(`~!@#$%^&*()-_=+|[{}];:<,.\>/?)组成
        const reg = /^[0-9a-z`~!@#$%^&*()\-_=+|[{}\];:<,.\\>\/ ?]+$/i;
        if (!reg.test(control.value)) {
            return { chapName: true };
        }

        return null;
    }

    /**
     * 验证Chap密码合法性
     * 必须包含大写字母、小写字母、数字和特殊字符(`~!@#$%^&*()-_=+|[{}];:<,.\>/?和空格)中任意三者的组合
     * @param control 表单组件
     */
    validateChapPassword(control: AbstractControl): ValidationErrors {
        const valueStr: string = control.value.toString();

        const upperCaseReg = /[A-Z]+/;
        const lowerCaseReg = /[a-z]+/;
        const numberReg = /[0-9]+/;
        const specialReg = /[`~!@#$%^&*()\-_=+|[{}\];:<,.\\>\/ ? ]+/;

        let complex = 0;

        if (upperCaseReg.test(valueStr)) {
            complex++;
        }

        if (lowerCaseReg.test(valueStr)) {
            complex++;
        }

        if (numberReg.test(valueStr)) {
            complex++;
        }

        if (specialReg.test(valueStr)) {
            complex++;
        }

        if (complex < 3) {
            return { chapPassword: true };
        } else {
            return null;
        }
    }

    /**
     * 校验启动器认证目标器确认密码
     * @param control 表单组件
     */
    validateConfirm(targetName: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors => {
            const parentCtrl = control.parent;
            if (!parentCtrl) {
                return null;
            }

            const passwordCtrl = parentCtrl.get(targetName);

            if (passwordCtrl.value !== control.value) {
                return { confirmPassword: true };
            }

            return null;
        };
    }

    /**
     * 校验启动器认证目标器确认密码
     * @param control 表单组件
     */
    validateEqual(targetName: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors => {
            const parentCtrl = control.parent;

            if (!parentCtrl) {
                return null;
            }

            const passwordCtrl = parentCtrl.get(targetName);

            if (passwordCtrl.value === control.value) {
                return { equal: true };
            }

            return null;
        };
    }

    /**
     * 触发其他表单组件校验
     * @param targetName 目标名称
     * @param validateNull 目标组件为空时是否校验，默认不校验
     */
    validateTargets(targetNames: string[], validateNull = false): ValidatorFn {
        return (control: AbstractControl): ValidationErrors => {
            const root = control.root;
            targetNames.forEach((name) => {
                const target = root.get(name) as FormControl;

                if (target && (target.value || validateNull)) {
                    target.validate();
                }
            });

            return null;
        };
    }

    /**
     * 计算容量利用率(与LUN/LUNGROUP保持一致)
     * @param data 包含容量相关字段的数据
     */
    usageValue(data): number {
        const capacity = Number(data.capacity) || 0;
        const allocatedCapacity = Number(data.allocatedCapacity) || 0;

        return capacity && allocatedCapacity / capacity * 100;
    }

    /**
     * 查询主机LUN ID
     * @param {*} param 参数对象，包含5种字段组合下发场景：
     *                   1：主机ID
     *                   2：主机组ID
     *                   3：主机ID和LUNID
     *                   4：主机ID和LUNGroupId
     *                   5：主机组ID和LUNGroupId
     * @param {{ [key: string]: any }} [filter] 过滤参数对象
     * @returns REST接口
     * @memberof MappingService
     */
    getHostLunRest(param: { [key: string]: any }, filter?: Filter[]): Rest {
        const rest = this.http.makeUrl('mapping.hostLunInfo');
        rest.setParams(param);
        if (filter) {
            rest.setFilters(filter);
        }
        return rest;
    }

    /**
     * 更新结束编号
     * @param countCtrl 数量配置
     * @param startNoCtrl 起始编号配置
     */
    updataEndSuffix(countCtrl: FormControl, startNoCtrl: FormControl): string {
        const count = countCtrl.value;
        const startNo = startNoCtrl.value;
        const endSuffixNum = Number(startNo) + Number(count) - 1;
        const endSuffix = endSuffixNum.toString().padStart(startNo.length, '0');
        return endSuffix;
    }

    /**
     * Gets vstore rest
     * @returns
     */
    getVstoreRest(): Rest {
        return this.http.makeUrl('vstore.getBatch');
    }
}
